import React from "react";

export default function SofttenantExamples() {
  const setupCode = `pip install softtenant

from fastapi import FastAPI
from sqlalchemy import create_engine
from softtenant import TenantMiddleware, TenantSession

engine = create_engine("postgresql://user:pass@localhost/mydb")
app = FastAPI()
app.add_middleware(TenantMiddleware, engine=engine, resolver="header")`;

  const modelCode = `from softtenant import TenantMixin
from sqlalchemy.orm import DeclarativeBase

class Base(DeclarativeBase):
    pass

class Order(TenantMixin, Base):
    __tablename__ = "orders"
    id: int
    item: str`;

  const routeCode = `from fastapi import Depends
from softtenant import get_tenant_session
from sqlalchemy.orm import Session

@app.get("/orders")
def list_orders(db: Session = Depends(get_tenant_session)):
    return db.query(Order).all()`;

  const tenantACurl = `# Tenant A requests — only sees Tenant A's orders
curl http://localhost:8000/orders \\
  -H "X-Tenant-ID: tenant-a"`;

  const tenantAOutput = `[
  { "id": 1, "item": "Widget", "tenant_id": "tenant-a" },
  { "id": 2, "item": "Gadget", "tenant_id": "tenant-a" }
]`;

  const tenantBCurl = `# Tenant B requests — only sees Tenant B's orders
curl http://localhost:8000/orders \\
  -H "X-Tenant-ID: tenant-b"`;

  const tenantBOutput = `[
  { "id": 3, "item": "Doohickey", "tenant_id": "tenant-b" }
]`;

  const jwtCurl = `# Using JWT resolver instead of header
curl http://localhost:8000/orders \\
  -H "Authorization: Bearer eyJhbGciOi...tenant_id:tenant-a..."`;

  return (
    <main className="docs-wrap">
      <div className="docs-layout">
        <aside className="docs-toc">
          <strong>On this page</strong>
          <a href="#setup">Setup</a>
          <a href="#model">Tenant Model</a>
          <a href="#route">Scoped Route</a>
          <a href="#isolation">Tenant Isolation</a>
          <a href="#jwt">JWT Resolver</a>
        </aside>

        <article>
          <header className="stt-hero">
            <div className="stt-pill">softtenant • Examples</div>
            <h1 className="stt-h1">Multi-tenancy with one middleware</h1>
            <p className="stt-lead">
              See how row-level isolation works without touching your business logic.
            </p>
          </header>

          <section id="setup" className="stt-section stt-card">
            <h2 className="stt-card-title">Setup</h2>
            <div className="stt-card-body">
              <div className="stt-code"><pre className="stt-pre">{setupCode}</pre></div>
            </div>
          </section>

          <section id="model" className="stt-section stt-card">
            <h2 className="stt-card-title">Tenant Model</h2>
            <div className="stt-card-body">
              <p className="stt-p">
                Mix in <code>TenantMixin</code> — it adds the <code>tenant_id</code> column. Everything else stays the same.
              </p>
              <div className="stt-code"><pre className="stt-pre">{modelCode}</pre></div>
            </div>
          </section>

          <section id="route" className="stt-section stt-card">
            <h2 className="stt-card-title">Scoped Route</h2>
            <div className="stt-card-body">
              <p className="stt-p">
                Swap <code>get_db</code> for <code>get_tenant_session</code>. No other changes.
              </p>
              <div className="stt-code"><pre className="stt-pre">{routeCode}</pre></div>
            </div>
          </section>

          <section id="isolation" className="stt-section stt-grid-2">
            <article className="stt-card">
              <h2 className="stt-card-title">Tenant A</h2>
              <div className="stt-card-body">
                <div className="stt-code"><pre className="stt-pre">{tenantACurl}</pre></div>
                <div className="stt-output"><pre className="stt-pre">{tenantAOutput}</pre></div>
              </div>
            </article>
            <article className="stt-card">
              <h2 className="stt-card-title">Tenant B</h2>
              <div className="stt-card-body">
                <div className="stt-code"><pre className="stt-pre">{tenantBCurl}</pre></div>
                <div className="stt-output"><pre className="stt-pre">{tenantBOutput}</pre></div>
              </div>
            </article>
          </section>

          <section id="jwt" className="stt-section stt-card">
            <h2 className="stt-card-title">JWT Resolver</h2>
            <div className="stt-card-body">
              <p className="stt-p">
                Resolve tenant from a JWT claim — no header needed.
              </p>
              <div className="stt-code"><pre className="stt-pre">{jwtCurl}</pre></div>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}
